import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { IoCloseOutline, IoChevronForwardOutline } from 'react-icons/io5';
import { formatDateOnly } from '../utils/dateUtils';

interface ToDoItem {
  title: string;
  description?: string;
  due_date?: string;
  priority?: 'Urgent' | 'High' | 'Medium' | 'Low';
}

interface CardWithToDos {
  card: {
    id: string;
    title: string;
    type: string;
  };
  todos: ToDoItem[];
}

interface CardWithFollowUps {
  card: {
    id: string;
    title: string;
    type: string;
  };
  followUps: ToDoItem[];
}

interface ToDoModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeToDos: CardWithToDos[];
  activeFollowUps: CardWithFollowUps[];
  isLoading: boolean;
  onCardClick: (cardId: string) => void;
}

const ToDoModal: React.FC<ToDoModalProps> = ({
  isOpen,
  onClose,
  activeToDos,
  activeFollowUps,
  isLoading,
  onCardClick
}) => {
  const renderPriorityBadge = (priority: string) => {
    const priorityClasses = {
      Urgent: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
      High: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
      Medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      Low: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
    };

    return (
      <div className={`text-xs font-medium rounded-full inline-flex px-2 py-0.5 mt-1 ${
        priorityClasses[priority as keyof typeof priorityClasses]
      }`}>
        {priority}
      </div>
    );
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 dark:bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 dark:text-white flex justify-between items-center"
                >
                  <span>Active To-Dos & Follow-Ups</span>
                  <button
                    onClick={onClose}
                    className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none"
                    title="Close modal"
                  >
                    <IoCloseOutline size={20} />
                  </button>
                </Dialog.Title>
                
                {isLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <div className="mt-4 space-y-6">
                    {/* Project To-Dos */}
                    {activeToDos.length > 0 && (
                      <div>
                        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                          Project To-Dos
                        </h4>
                        <div className="space-y-4">
                          {activeToDos.map((todoItem) => (
                            <div key={todoItem.card.id} className="border rounded-lg p-4 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                              <button 
                                onClick={() => onCardClick(todoItem.card.id)}
                                className="text-left w-full flex justify-between items-center mb-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                              >
                                <span className="font-medium">{todoItem.card.title}</span>
                                <IoChevronForwardOutline size={16} />
                              </button>
                              <div className="space-y-3">
                                {todoItem.todos.map((todo, index) => (
                                  <div key={index} className="pl-4 border-l-2 border-gray-300 dark:border-gray-600">
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <h5 className="font-medium text-gray-900 dark:text-white">{todo.title}</h5>
                                        {todo.description && (
                                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{todo.description}</p>
                                        )}
                                        {todo.due_date && (
                                          <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                            Due: {formatDateOnly(todo.due_date)}
                                          </div>
                                        )}
                                        {todo.priority && renderPriorityBadge(todo.priority)}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* People Follow-Ups */}
                    {activeFollowUps.length > 0 && (
                      <div>
                        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                          People Follow-Ups
                        </h4>
                        <div className="space-y-4">
                          {activeFollowUps.map((followUpItem) => (
                            <div key={followUpItem.card.id} className="border rounded-lg p-4 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                              <button 
                                onClick={() => onCardClick(followUpItem.card.id)}
                                className="text-left w-full flex justify-between items-center mb-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                              >
                                <span className="font-medium">{followUpItem.card.title}</span>
                                <IoChevronForwardOutline size={16} />
                              </button>
                              <div className="space-y-3">
                                {followUpItem.followUps.map((followUp, index) => (
                                  <div key={index} className="pl-4 border-l-2 border-gray-300 dark:border-gray-600">
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <h5 className="font-medium text-gray-900 dark:text-white">{followUp.title}</h5>
                                        {followUp.description && (
                                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{followUp.description}</p>
                                        )}
                                        {followUp.due_date && (
                                          <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                            Due: {formatDateOnly(followUp.due_date)}
                                          </div>
                                        )}
                                        {followUp.priority && renderPriorityBadge(followUp.priority)}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {activeToDos.length === 0 && activeFollowUps.length === 0 && (
                      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                        <p>No active to-dos or follow-ups found.</p>
                        <p className="mt-2">Create some in your Project or People cards!</p>
                      </div>
                    )}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ToDoModal; 